/**
 * 보고서를 발표자료 슬라이드로 나눈다. 앱은 이것을 pptxgenjs 로 옮기기만 한다 — 여기 두어야 시험할 수 있다.
 *
 * 보고서와 같은 골격(국가직 팀장 검토, 2026-10-23): 표지(주제·기준일·작성 부서) → 요약(제목이 판단) →
 * 그림마다 한 장(제목이 그 그림의 결론) → 확인 필요 사항 → 향후 조치(안). 슬라이드 제목만 이어 읽어도
 * 보고가 된다. 원자료는 「전체 목록」을 청했을 때만 별첨 한 장.
 */
import type { ReportChart, ReportDocument, TableData } from './files';
import {
  cellText, exceptionCaption, figureCaption, notedItems, noticeItems, noticeSign, noticeTables, reportFigures, sectionTitles,
  STATUS_CAPTION, tablesAt,
} from './reportHtml';

type SlideTable = { title: string; columns: string[]; rows: string[][] };

/**
 * 슬라이드 한 장. 보고용(한국형)은 `topic`(번호 붙은 주제 제목, 명사구) 아래 `title`(거버닝 메시지, 그 장의 결론 한 줄)을 둔다.
 * 마케팅형은 `statement`(한 문장), `number`(큰 숫자) 장과 강조 막대 하나(`emphasis`)를 쓴다. `size`는 그리기 전에 글자 길이로 고른
 * 글자 크기(pt)다(`fitSize`).
 */
export type ReportSlide =
  | { kind: 'title'; title: string; subtitle: string }
  | { kind: 'points'; title: string; items: string[]; figures?: { label: string; value: string }[]; table?: SlideTable; topic?: string; size?: number }
  | { kind: 'chart'; title: string; caption?: string; chart: ReportChart; topic?: string; emphasis?: number; size?: number }
  | { kind: 'table'; title: string; columns: string[]; rows: string[][]; topic?: string }
  | { kind: 'statement'; title: string; caption?: string; size: number }
  | { kind: 'number'; value: string; title: string; size: number };

/** 발표자료 꼴. 보고용(한국형 보고서 장표)과 마케팅형(키노트처럼 어두운 바탕, 한 장 한 생각). */
export type Deck = 'report' | 'keynote';

/** 장 번호(Ⅰ. 요약, Ⅱ-1. …). */
const ROMAN = ['Ⅰ', 'Ⅱ', 'Ⅲ', 'Ⅳ', 'Ⅴ', 'Ⅵ', 'Ⅶ', 'Ⅷ'];

/** 글자 폭(em): 한글·한자·전각은 1, 공백은 0.3, 그 밖(숫자, 라틴, 부호)은 0.55. 맑은 고딕 기준 어림이다. */
export function textEm(text: string): number {
  let width = 0;
  for (const char of text) {
    if (/\s/.test(char)) width += 0.3;
    else if (/[ᄀ-ᇿ　-鿿가-힯＀-￯]/.test(char)) width += 1;
    else width += 0.55;
  }
  return width;
}

/**
 * 그리기 전에 글자 크기를 고른다. 너비(인치)에 들어가는 줄 수가 `lines` 이하인 가장 큰 크기, 없으면 가장 작은 크기. 줄바꿈 손실로
 * 1.1배를 더한다. pptxgenjs의 글자 줄이기(`fit: 'shrink'`)는 파일을 열 때 계산되지 않는다(조사 2026-10-28, gen-xml.ts).
 */
export function fitSize(text: string, width: number, sizes: number[], lines: number): number {
  const em = textEm(text) * 1.1;
  return sizes.find((size) => Math.ceil(em / ((width * 72) / size)) <= lines) ?? sizes[sizes.length - 1] ?? 18;
}

/** 표 이름·그림 이름에서 괄호 풀이(「(단위: 건)」, 「(상위 6)」)를 뗀 명사구. */
function nounOf(caption: string): string {
  return caption.replace(/^〈[^〉]*〉\s*/, '').replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/**
 * 그림의 결론 줄. 작성 담당이 그림 결론을 안 썼으면 코드가 쓴다: 가장 큰 막대와 그 몫. 그림 이름(「…별 건수(상위 6)」)이 결론 자리에
 * 되풀이됐다(시험 덱, 2026-10-28).
 */
function chartLine(chart: ReportChart): string {
  if (chart.message) return chart.message;
  if (chart.kind === 'line' || chart.kind === 'share' || chart.categories.length < 2) return chart.title;
  const values = chart.categories.map((_, at) => chart.series.reduce((sum, series) => sum + (series.values[at] ?? 0), 0));
  // 그림은 대개 상위 몇 개만 담는다. 그 몫(「그림의 6개 86건 중 86%」)은 요약의 전체 몫(71.2%)과 어긋나 보여 쓰지 않는다.
  return topLine(chart.categories, values, chart.unit ?? '')?.replace(/\(.*\)$/, '') ?? chart.title;
}

/** 더해서 몫을 말할 수 있는 단위. 일수, 비율은 더하면 뜻이 없다(독립 검토: 「그림의 6개 180일 중 25%」). */
const ADDITIVE = /^(?:건|명|원|개|대|호기|곳|회|천원|만원|백만원)$/;

/** 「가나 40건으로 가장 많음」. 일수·비율처럼 더할 수 없는 단위는 「가장 큼」. 값이 다 0이면 없음. */
function topLine(names: string[], values: number[], unit: string): string | undefined {
  const sum = values.reduce((total, value) => total + value, 0);
  if (!sum || !names.length) return undefined;
  const top = values.reduce((best, value, at) => (value > (values[best] ?? 0) ? at : best), 0);
  const number = (value: number) => `${value.toLocaleString('en-US')}${unit}`;
  return `${names[top]} ${instrumental(number(values[top] ?? 0))} 가장 ${ADDITIVE.test(unit) ? '많음' : '큼'}`;
}

/**
 * 「으로/로」: 받침이 없거나 ㄹ 받침이면 로(「3일로」), 그 밖의 받침이면 으로(「40건으로」). 숫자로 끝나면 읽는 소리로 가린다
 * (0 십·백·천·만, 3 삼, 6 육은 으로, 나머지는 로). 그 밖은 로.
 */
function instrumental(word: string): string {
  const last = word.trim().slice(-1);
  if (/\d/.test(last)) return `${word}${/[036]/.test(last) ? '으로' : '로'}`;
  const code = last.charCodeAt(0);
  if (Number.isNaN(code) || code < 0xac00 || code > 0xd7a3) return `${word}로`;
  const final = (code - 0xac00) % 28;
  return `${word}${final === 0 || final === 8 ? '로' : '으로'}`;
}

/**
 * 표 장의 결론 줄(보고용 거버닝 메시지). 첫 칸이 이름이고 둘째 칸이 수인 표에서 가장 큰 줄과 그 몫(끝 줄이 「계」면 그 값이 전체).
 * 표 장에 결론 줄이 없었다(독립 검토). 수 칸이 아니면 없음.
 */
function tableLine(caption: string, table: TableData): string | undefined {
  const unit = /단위:\s*([^,)]+)/.exec(caption)?.[1]?.trim() ?? '';
  const rows = table.rows.filter((row) => !/^계$|^그 밖/.test(cellText(row[0])));
  const values = rows.map((row) => Number(String(row[1] ?? '').replace(/,/g, '')));
  if (rows.length < 2 || values.some((value) => !Number.isFinite(value))) return undefined;
  const total = table.rows.find((row) => cellText(row[0]) === '계');
  const sum = total ? Number(String(total[1] ?? '').replace(/,/g, '')) : undefined;
  const line = topLine(rows.map((row) => cellText(row[0])), values, unit);
  // 몫은 「계」 줄이 있을 때만 전체 기준으로 쓴다. 상위 몇 줄만 담은 표의 몫은 전체 몫과 어긋나 보인다.
  if (!line || !sum || !ADDITIVE.test(unit)) return line;
  const top = values.reduce((best, value, at) => (value > (values[best] ?? 0) ? at : best), 0);
  const share = Math.round(((values[top] ?? 0) / sum) * 1000) / 10;
  return `${line}(전체 ${sum.toLocaleString('en-US')}${unit} 중 ${share}%)`;
}

/** 마케팅형으로 그리는 보고서인가. 장 구성과 바탕색이 같은 판정을 쓴다(독립 검토: 공지와 옛 보고서가 어두운 바탕으로 나갈 뻔했다). */
export function keynoteDeck(report: Pick<ReportDocument, 'basis' | 'deck' | 'genre'>): boolean {
  return !!report.basis && report.deck === 'keynote' && report.genre !== 'notice';
}

/** 보고용 거버닝 메시지 줄의 너비(인치)와 크기 후보. 넘치면 두 줄까지 쓰고 더 작게 한다. */
const MESSAGE_WIDTH = 12.33;
const MESSAGE_SIZES = [18, 16, 14];

/** 한 장에 읽히는 표의 크기. */
const SLIDE_ROWS = 10;
const SLIDE_COLUMNS = 6;
/**
 * 목록의 상위 명단 표는 끝 동점을 셋까지 더 싣는다(listTables). 그 줄을 자르면 표 이름(「상위 12명」)과 줄 수가 어긋난다(실측
 * 2026-10-27). 12pt 열세 줄은 한 장에 들어간다.
 */
const TOP_ROWS = SLIDE_ROWS + 3;

function slideTable(title: string, table: TableData, limit = SLIDE_ROWS): SlideTable {
  const columns = table.columns.slice(0, SLIDE_COLUMNS);
  return {
    title,
    columns,
    rows: table.rows.slice(0, limit).map((row) => columns.map((_, index) => cellText(row[index]))),
  };
}

/**
 * 발표자료 표는 10줄 — 〈표 2〉가 넘치면 아래쪽 이름 줄부터 뺀다. 소계 줄과 「계」는 남긴다 — 묶음 다섯에 현장 여섯이면
 * 11줄이 되어 「계」가 잘렸다(2026-10-25 코드 리뷰). 앞 묶음의 이름(쏠림 줄이 말한 이름 묶음 현장)이 가장 오래 남는다(9차 검토).
 */
function folded(table: TableData): TableData {
  if (table.rows.length <= SLIDE_ROWS) return table;
  const rows = table.rows.slice();
  const kept = (row: unknown[] | undefined) => /소계$|^계$/.test(String(row?.[0] ?? ''));
  for (let at = rows.length - 2; at >= 0 && rows.length > SLIDE_ROWS; at -= 1) {
    if (!kept(rows[at])) rows.splice(at, 1);
  }
  return { ...table, rows };
}

/**
 * 묶음별 현황 표를 10줄에 맞춘다. 끝 묶음들을 「그 밖 N개 ○○」 한 줄로 합치고 「계」를 남긴다. 줄만 빼면 「계」가 보이는 줄의 합과
 * 달라진다. 이미 접힌 「그 밖 3개 부서」 줄은 묶음 셋으로 센다.
 */
function foldedGroups(table: TableData): TableData {
  if (table.rows.length <= SLIDE_ROWS) return table;
  const last = table.rows[table.rows.length - 1];
  const head = table.rows.slice(0, SLIDE_ROWS - 2);
  const rest = table.rows.slice(SLIDE_ROWS - 2, -1);
  const count = rest.reduce<number>((sum, row) => sum + Number(/^그 밖 (\d+)개/.exec(String(row[0] ?? ''))?.[1] ?? 1), 0);
  const summed = table.columns.slice(1).map((_, at) => Math.round(rest.reduce<number>((sum, row) => sum + (Number(row[at + 1]) || 0), 0) * 100) / 100);
  return { ...table, rows: [...head, [`그 밖 ${count}개 ${table.columns[0] ?? ''}`.trim(), ...summed], ...(last ? [last] : [])] };
}

/** 목록 표 한 장. 제목은 표 이름(괄호 앞), 표 머리는 〈표 n〉 이름 전체. */
function listSlide(one: NonNullable<ReportDocument['tables']>[number], number: number): ReportSlide {
  return {
    kind: 'points', title: one.caption.replace(/\s*\(.*\)\s*$/, ''), items: [],
    table: one.role === 'groups' ? slideTable(`〈표 ${number}〉 ${one.caption}`, foldedGroups(one.table))
      : slideTable(`〈표 ${number}〉 ${one.caption}`, one.table, TOP_ROWS),
  };
}

/** 공지를 발표자료로: 표지(게시일·발신 부서), 번호 항목 한 장, 〈참고〉 표와 그림. PDF와 같은 차례다. */
function noticeSlides(report: ReportDocument): ReportSlide[] {
  return [
    { kind: 'title', title: report.title, subtitle: noticeSign(report).join(' · ') },
    { kind: 'points', title: report.title, items: noticeItems(report) },
    ...noticeTables(report).map((one): ReportSlide => ({
      kind: 'points', title: one.caption.replace(/\s*\(.*\)\s*$/, ''), items: [], table: slideTable(one.caption, foldedGroups(one.table)),
    })),
    ...(report.charts ?? []).map((chart, index): ReportSlide => ({
      kind: 'chart', title: chart.message ?? chart.title, caption: figureCaption(chart, index + 1), chart,
    })),
  ];
}

/**
 * 분량을 정한 보고서: 공문서 골격. 한국형 보고서 장표다(사용자 결정, 2026-10-28): 장마다 번호 붙은 주제 제목(명사구) 아래 결론 한
 * 줄(거버닝 메시지, 슬라이드의 `title`). 주제 제목은 Ⅰ. 요약, Ⅱ-n. 현황(표와 그림), Ⅲ. 확인 필요 사항, Ⅳ. 향후 조치(안) 차례다.
 * 거버닝 메시지만 이어 읽어도 보고가 된다.
 */
function officialSlides(report: ReportDocument): ReportSlide[] {
  if (report.genre === 'notice') return noticeSlides(report);
  let tables = 0;
  const points = report.points;
  const titles = sectionTitles(report);
  let section = 0;
  let item = 0;
  const topicOf = (noun: string) => `${ROMAN[section++] ?? section}. ${noun}`;
  // 현황 묶음은 장 번호 하나에 Ⅱ-1, Ⅱ-2로 나간다.
  let state = -1;
  const stateTopic = (noun: string) => {
    if (state < 0) state = section++;
    item += 1;
    return `${ROMAN[state] ?? state + 1}-${item}. ${noun}`;
  };
  const sized = (title: string) => fitSize(title, MESSAGE_WIDTH, MESSAGE_SIZES, 2);
  const slides: ReportSlide[] = [{
    kind: 'title',
    title: report.title,
    subtitle: [report.basis ? `(${report.basis})` : '', report.author ?? ''].filter(Boolean).join(' · '),
  }];
  const summary = points?.summary ?? [report.summary];
  const lead = summary[0] ?? report.title;
  slides.push({
    kind: 'points',
    topic: topicOf(titles.summary),
    title: lead,
    size: sized(lead),
    // 첫 줄은 제목이 되고 그 줄의 각주(갈래 숫자를 센 칸)는 맨 앞 항목이 된다(8차 검토).
    items: [...notedItems(summary, report).slice(1), ...(report.statusTable && report.statusNote ? [`* ${report.statusNote}`] : [])],
    ...(report.statusTable
      ? { table: slideTable(`〈표 ${++tables}〉 ${STATUS_CAPTION}`, report.statusTable) }
      : { figures: reportFigures(report.kpis) }),
  });
  // 경과 표는 제 장 — 확인 필요 장에 표와 글을 겹치면 읽기 어려웠다(3차 검토). 제목은 결론 — 쏠림 줄이 있으면
  // 그것(5차 검토), 없으면 「예정일 경과 미실시 52건」.
  const exceptions = report.exceptions;
  if (exceptions) {
    const [focus, ...more] = exceptions.points ?? [];
    const title = focus ?? `${exceptions.title} ${exceptions.total.toLocaleString('en-US')}건`;
    slides.push({
      kind: 'points', topic: stateTopic(exceptions.title), title, size: sized(title), items: more,
      table: slideTable(exceptionCaption(exceptions, ++tables), folded(exceptions.table)),
    });
  }
  // 목록 표: 묶음별 현황은 그림 앞, 상위 명단은 그림 뒤(PDF와 같은 차례).
  // 표 장도 결론 줄을 둔다(가장 큰 줄과 그 몫). 못 쓰면 주제 제목만.
  const listed = (one: NonNullable<ReportDocument['tables']>[number]) => {
    const slide = listSlide(one, ++tables);
    const line = tableLine(one.caption, one.table);
    return slide.kind === 'points'
      ? { ...slide, topic: stateTopic(nounOf(one.caption)), ...(line ? { title: line, size: sized(line) } : {}) } : slide;
  };
  tablesAt(report, 'groups').forEach((one) => slides.push(listed(one)));
  (report.charts ?? []).forEach((chart, index) => {
    const title = chartLine(chart);
    slides.push({
      kind: 'chart', topic: stateTopic(nounOf(chart.title)), title, size: sized(title), caption: figureCaption(chart, index + 1), chart,
    });
  });
  tablesAt(report, 'top').forEach((one) => slides.push(listed(one)));
  const checks = points?.checks ?? [];
  const actions = points?.actions ?? [];
  const marked = (list: string[], mark: string) => notedItems(list, report)
    .map((one) => (one.startsWith('* ') ? one : `${mark} ${one}`));
  // 확인 사항이 한 줄이면 조치 장에 합친다 — 한 줄짜리 장이 생겼다(6차 검토).
  if (checks.length === 1 && actions.length) {
    const title = `${titles.checks}·${titles.actions}`;
    slides.push({
      kind: 'points', topic: topicOf(title), title,
      items: [...marked(checks, '(확인)'), ...marked(actions, '(조치)')],
    });
  } else {
    if (checks.length) {
      const title = checks[0] ?? titles.checks;
      slides.push({ kind: 'points', topic: topicOf(titles.checks), title, size: sized(title), items: notedItems(checks, report).slice(1) });
    }
    if (actions.length) slides.push({ kind: 'points', topic: topicOf(titles.actions), title: titles.actions, items: notedItems(actions, report) });
  }
  const table = report.table;
  if (report.detailRows === undefined && table?.rows.length) {
    const shown = Math.min(table.rows.length, SLIDE_ROWS);
    // 별첨은 결론 줄 없이 주제 제목만(건수는 제목에).
    const name = `전체 목록(${shown}/${table.rows.length.toLocaleString('en-US')}건)`;
    slides.push({ kind: 'table', ...slideTable(name, table), topic: `별첨. ${name}` });
  }
  return slides;
}

/** 마케팅형 한 문장 장의 너비(인치)와 크기 후보(세 줄 안). 큰 숫자 장의 크기 후보. */
const STATEMENT_SIZES = [44, 40, 36, 32, 28];
const NUMBER_SIZES = [120, 96, 80, 64];

/**
 * 막대 그림을 강조 막대 하나로 바꾼다: 계열을 범주마다 더한 값 하나, 가장 큰 범주(코드가 센 차례의 첫째)만 강조색. 마케팅형은 회색에
 * 강조색 하나가 규칙이다(조사 2026-10-28: Knaflic, huashu-design). 선 그림과 구성 막대는 그대로 둔다.
 */
function emphasized(chart: ReportChart): { chart: ReportChart; emphasis?: number } {
  if (chart.kind === 'line' || chart.kind === 'share' || !chart.categories.length) return { chart };
  const values = chart.categories.map((_, at) => chart.series.reduce((sum, series) => sum + (series.values[at] ?? 0), 0));
  const top = values.reduce((best, value, at) => (value > (values[best] ?? 0) ? at : best), 0);
  return {
    chart: { ...chart, kind: undefined, series: [{ name: chart.series.length === 1 ? chart.series[0]?.name ?? '건수' : '합계', values }] },
    // 값이 다 0이면 강조할 막대가 없다.
    ...(values.some((value) => value > 0) ? { emphasis: top } : {}),
  };
}

/**
 * 마케팅형(애플 키노트처럼): 어두운 바탕, 한 장 한 생각. 표지 → 결론 한 문장 → 큰 숫자(셋까지) → 그림마다 한 장(강조 막대 하나,
 * 제목은 그 그림의 결론) → 나머지 요약 한 문장씩 → 다음 할 일(셋까지) → 참고 표. 숫자와 문장은 보고용과 같은 보고서 자료다.
 */
function keynoteSlides(report: ReportDocument): ReportSlide[] {
  const width = MESSAGE_WIDTH;
  const statement = (title: string, caption?: string): ReportSlide => ({
    kind: 'statement', title, size: fitSize(title, width, STATEMENT_SIZES, 3), ...(caption ? { caption } : {}),
  });
  const summary = report.points?.summary ?? [report.summary];
  const [lead, ...rest] = summary.filter((line) => line.trim());
  const slides: ReportSlide[] = [{
    kind: 'title',
    title: report.title,
    subtitle: [report.basis ? `(${report.basis})` : '', report.author ?? ''].filter(Boolean).join(' · '),
  }];
  if (lead) slides.push(statement(lead, report.period || undefined));
  // 같은 값의 숫자(전체 104건, 미처리 104건)는 한 장에 이름을 합친다.
  const numbers: { value: string; labels: string[] }[] = [];
  reportFigures(report.kpis).forEach((figure) => {
    const same = numbers.find((one) => one.value === figure.value);
    if (same) same.labels.push(figure.label);
    else numbers.push({ value: figure.value, labels: [figure.label] });
  });
  numbers.slice(0, 3).forEach((one) => slides.push({
    kind: 'number', value: one.value, title: one.labels.join(', '), size: fitSize(one.value, width / 2, NUMBER_SIZES, 1),
  }));
  (report.charts ?? []).forEach((chart, index) => {
    const drawn = emphasized(chart);
    // 제목은 강조 막대를 말해야 한다. 작성 담당의 결론이 그 범주를 말하지 않으면 코드 결론 줄을 쓴다(실측 08:49: 강조 막대는 74건
    // 현장인데 제목은 「각각 3건」인 두 현장 이야기였다).
    const named = drawn.emphasis !== undefined ? chart.categories[drawn.emphasis] : undefined;
    const title = chart.message && (!named || chart.message.includes(named)) ? chart.message : chartLine({ ...chart, message: undefined });
    slides.push({
      kind: 'chart', title, size: fitSize(title, width, [32, 28, 24], 2), caption: figureCaption(chart, index + 1), ...drawn,
    });
  });
  rest.forEach((line) => slides.push(statement(line)));
  // 확인할 것은 첫 줄만 한 문장 장으로(나머지는 참고에). 보고용과 같은 보고서 자료에서 빠뜨리지 않는다(독립 검토).
  const checks = report.points?.checks ?? [];
  if (checks[0]) slides.push(statement(checks[0], sectionTitles(report).checks));
  const actions = (report.points?.actions ?? []).slice(0, 3);
  if (actions.length) slides.push({ kind: 'points', title: '다음 할 일', items: actions, size: 24 });
  // 표와 나머지 확인 사항은 끝에 참고로 둔다. 숫자를 확인하려는 사람이 있다.
  const reference = (title: string, table: TableData) => {
    const shown = slideTable(title, table, TOP_ROWS);
    slides.push({ kind: 'table', topic: '참고', title, columns: shown.columns, rows: shown.rows });
  };
  if (report.statusTable) reference(STATUS_CAPTION, report.statusTable);
  if (report.exceptions) reference(exceptionCaption(report.exceptions, 1).replace(/^〈표 \d+〉\s*/, ''), folded(report.exceptions.table));
  [...tablesAt(report, 'groups'), ...tablesAt(report, 'top')].forEach((one) => reference(one.caption, one.table));
  if (checks.length > 1) slides.push({ kind: 'points', title: `참고: ${sectionTitles(report).checks}`, items: checks.slice(1), size: 20 });
  return slides;
}

/**
 * 차트 XML의 한 단계짜리 다단계 범주(`c:multiLvlStrRef`)를 일반 범주(`c:strRef`)로 바꾼다. pptxgenjs는 막대 그림의 범주를 늘
 * 다단계로 쓰는데, 이것을 못 읽는 뷰어(한쇼, 일부 미리보기)는 범주 이름 대신 번호를 띄운다(사용자, 2026-10-28: 「차트가 왼쪽에 숫자로만
 * 나와서 뭔지 알기 힘들다」). 단계가 둘 이상이면 그대로 둔다.
 */
export function plainCategoryXml(xml: string): string {
  return xml.replace(
    /<c:multiLvlStrRef>\s*<c:f>([^<]*)<\/c:f>\s*<c:multiLvlStrCache>\s*(<c:ptCount val="\d+"\/>)\s*<c:lvl>((?:(?!<c:lvl>)[\s\S])*?)<\/c:lvl>\s*<\/c:multiLvlStrCache>\s*<\/c:multiLvlStrRef>/g,
    (_, formula: string, count: string, points: string) => `<c:strRef><c:f>${formula}</c:f><c:strCache>${count}${points.trim()}</c:strCache></c:strRef>`,
  );
}

/** 발표자료 장 나누기. 보고서의 `deck`이 마케팅형이면 키노트처럼, 아니면 한국형 보고서 장표다. 공지는 늘 공지 꼴이다. */
export function reportSlides(report: ReportDocument): ReportSlide[] {
  if (keynoteDeck(report)) return keynoteSlides(report);
  if (report.basis) return officialSlides(report);
  // 분량을 정하지 않은 예전 보고서 — 표지, 요약, 그림, 표.
  const slides: ReportSlide[] = [{
    kind: 'title',
    title: report.headline ?? report.title,
    subtitle: [report.period, new Date(report.generatedAt).toLocaleDateString('ko-KR')].filter(Boolean).join(' · '),
  }];
  const figures = reportFigures(report.kpis);
  if (figures.length || report.summary.trim()) {
    slides.push({ kind: 'points', title: report.summaryHeading ?? '요약', items: [report.summary], figures });
  }
  (report.charts ?? []).forEach((chart, index) => slides.push({
    kind: 'chart', title: chart.message ?? chart.title, caption: figureCaption(chart, index + 1), chart,
  }));
  const table = report.table;
  if (table?.rows.length && report.detailRows !== 0) {
    slides.push({ kind: 'table', ...slideTable(report.detailHeading ?? '상세 내역', table) });
  }
  return slides;
}
